<script setup>
  import {nextTick, ref} from "vue";

  import ComponentInput from "@components/Inputs/Input.vue";
  import ComponentTextarea from "@components/Textarea/Textarea.vue";
  import {useForm} from "@inertiajs/vue3";
  import {MedButton, MedLink, MedTooltip} from "@pharma4u/medicheck-components";

  defineOptions({
    name: "ComponentTodoListTodoItem",
  });

  const props = defineProps({
    todo: {
      type: Object,
      required: true,
    },
    patientId: {
      type: String,
      required: true,
    },
    /**
     * layer: embedded in layer
     */
    layer: {
      type: Boolean,
      default: false,
    },
  });

  const refLabel = ref(null);

  const expanded = ref(false);
  const showDescription = ref(false);
  const completed = ref(props.todo.completed_at !== null);
  const isHovered = ref(false);

  const form = useForm({
    completed_at: props.todo.completed_at !== null,
    label: props.todo.label,
    description: props.todo.description,
  });

  function openDetails() {
    expanded.value = true;
    nextTick(() => {
      refLabel.value.focus();
    });
  }

  function closeDetails(resetForm) {
    expanded.value = false;
    if (resetForm) {
      form.reset();
    }
  }

  function remove() {
    form.delete(route("todos.destroy", {patient: props.patientId, todo: props.todo.id}), {
      preserveScroll: true,
    });
  }

  function update() {
    form.patch(route("todos.update", {patient: props.patientId, todo: props.todo.id}), {
      preserveScroll: true,
      onSuccess: () => {
        closeDetails();
      },
    });
  }

  function optimisticTodos(pageProperties) {
    if (!pageProperties.patient?.todos) return {};

    return {
      patient: {
        ...pageProperties.patient,
        todos: pageProperties.patient.todos.map((t) =>
          t.id === props.todo.id ? {...t, completed_at: form.completed_at ? new Date().toISOString() : null} : t,
        ),
      },
    };
  }

  function setPending() {
    form.completed_at = false;

    form.patch(route("todos.update", {patient: props.patientId, todo: props.todo.id}), {
      preserveScroll: true,
      optimistic: optimisticTodos,
    });
  }

  function setComplete() {
    form.completed_at = true;

    form.patch(route("todos.update", {patient: props.patientId, todo: props.todo.id}), {
      preserveScroll: true,
      optimistic: optimisticTodos,
    });
  }

  // Optional: local hover state if Tailwind group-hover isn't giving the desired alignment
  const toggleHover = (state) => {
    isHovered.value = state;
  };
</script>

<template>
  <div
    class="relative flex items-start gap-0.5 rounded-md bg-gray-100 p-1 hover:bg-gray-200/60"
    :data-test="getTestId('item')"
  >
    <MedTooltip v-if="completed" content="auf nicht erledigt zurücksetzen">
      <MedButton
        aria-label="Als nicht erledigt markieren"
        flat
        variant="secondary"
        class="bg-transparent hover:bg-transparent"
        rounded="full"
        icon="check_circle"
        size="lg"
        :data-test="getTestId('item-set-pending-button')"
        @click="setPending()"
      />
    </MedTooltip>

    <MedTooltip v-else content="als erledigt markieren">
      <MedButton
        aria-label="Als erledigt markieren"
        flat
        class="size-auto min-h-auto border"
        rounded="full"
        size="lg"
        :icon="isHovered ? 'check_circle_outline' : 'radio_button_unchecked'"
        :icon-filled="!isHovered"
        :data-test="getTestId('item-set-complete-button')"
        @click="setComplete()"
        @mouseenter="toggleHover(true)"
        @mouseleave="toggleHover(false)"
        @focus="toggleHover(true)"
        @blur="toggleHover(false)"
      />
    </MedTooltip>

    <div class="@container w-full">
      <div class="w-full">
        <div class="flex min-h-8 justify-between space-x-1 empty:hidden">
          <MedLink
            v-if="!expanded"
            as="button"
            size="sm"
            class="flex-1 text-left"
            :data-test="getTestId('item-edit-details-link')"
            @click="openDetails()"
          >
            <MedTooltip content="bearbeiten">
              <div class="leading-tight hover:underline">{{ todo.label }}</div>
            </MedTooltip>
          </MedLink>

          <MedTooltip v-if="!expanded && todo.description" content="enthält Details">
            <div>
              <MedButton
                :aria-label="showDescription ? 'Details verbergen' : 'Details anzeigen'"
                flat
                size="xs"
                rounded="full"
                icon="notes"
                :data-test="getTestId('item-toggle-details-button')"
                @click="showDescription = !showDescription"
              />
            </div>
          </MedTooltip>

          <MedTooltip v-if="!expanded && !todo.url.endsWith($page.url)" content="Patientenakte öffnen">
            <MedButton
              aria-label="Patientenakte öffnen"
              as="inertia"
              flat
              size="xs"
              rounded="full"
              icon="launch"
              :href="todo.url"
              :data-test="getTestId('item-show-patient-button')"
            />
          </MedTooltip>
        </div>

        <div v-if="!expanded && showDescription" class="text-xs text-gray-600">{{ todo.description }}</div>

        <span v-if="!expanded && completed" class="block text-xs">erledigt {{ todo.completed_at }}</span>
      </div>
      <div v-if="expanded" class="space-y-2 bg-white px-2 pb-2">
        <ComponentInput
          ref="refLabel"
          v-model="form.label"
          :validation="form.errors.label"
          :hide-clear="true"
          :data-test="getTestId('item-label')"
        />

        <ComponentTextarea
          v-model="form.description"
          :validation="form.errors.description"
          label="Details"
          maxlength="255"
          rows="3"
          :data-test="getTestId('item-description')"
        />

        <div
          class="flex flex-col items-stretch space-y-4 space-x-0 @min-sm:flex-row-reverse @min-sm:space-y-0 @min-sm:space-x-4 @min-sm:space-x-reverse"
        >
          <MedButton
            variant="primary"
            :disabled="form.processing"
            :data-test="getTestId('item-details-save-button')"
            @click="update()"
          >
            Speichern
          </MedButton>

          <MedButton
            :disabled="form.processing"
            :data-test="getTestId('item-details-close-button')"
            @click="closeDetails(true)"
          >
            Abbrechen
          </MedButton>

          <MedButton
            flat
            variant="primary"
            :disabled="form.processing"
            :data-test="getTestId('item-details-delete-button')"
            @click="remove()"
          >
            Löschen
          </MedButton>
        </div>
      </div>
    </div>
  </div>
</template>
